import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarOff, Plus, ChevronLeft, ChevronRight } from "lucide-react";

// Helpers
const todayYmd = () => new Date().toISOString().slice(0, 10);
const ymd = (d) => new Date(d).toISOString().slice(0, 10);
const monthLabel = (d) => d.toLocaleString("en-IN", { month: "long", year: "numeric" });
const datesBetween = (a, b) => {
  const out = []; let d = new Date(a); const end = new Date(b);
  while (d <= end) { out.push(ymd(d)); d.setDate(d.getDate() + 1); }
  return out;
};

export default function LeaveManagement({ leaves = [], chefs = [], onDecide, onReload }) {
  // Admin → add chef leave form
  const [chefId, setChefId] = useState("");
  const [startDate, setStartDate] = useState(todayYmd());
  const [endDate, setEndDate] = useState(todayYmd());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Calendar month view
  const [cursor, setCursor] = useState(new Date());
  const [slotReqs, setSlotReqs] = useState([]);   // customer unavailable days
  const [mappings, setMappings] = useState([]);

  const loadAux = useCallback(async () => {
    try {
      const [sr, mp] = await Promise.all([
        api.get("/admin/slot-requests"),
        api.get("/admin/mappings"),
      ]);
      setSlotReqs(sr.data || []);
      setMappings(mp.data || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { loadAux(); }, [loadAux]);

  const submit = async (e) => {
    e.preventDefault();
    if (!chefId) { toast.error("Pick a chef"); return; }
    if (endDate < startDate) { toast.error("End date must be on or after start date"); return; }
    setBusy(true);
    try {
      await api.post("/admin/leaves", {
        chef_id: chefId,
        start_date: startDate,
        end_date: endDate,
        reason: reason || "Marked by admin",
      });
      toast.success("Chef leave saved & customers notified");
      setReason("");
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };

  // Build a date → events map for the current month grid
  const monthGrid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const startDow = first.getDay();         // 0=Sun
    const days = last.getDate();
    const cells = [];
    // pad leading
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return cells;
  }, [cursor]);

  const eventsByDate = useMemo(() => {
    const map = {};
    const push = (date, ev) => { (map[date] ||= []).push(ev); };
    leaves.filter(l => l.status === "approved").forEach(l => {
      datesBetween(l.start_date, l.end_date).forEach(d => push(d, { kind: "chef_leave", label: `Chef: ${l.chef_name}`, color: "bg-amber-100 text-amber-900 border-amber-300" }));
    });
    slotReqs.filter(s => s.status === "approved").forEach(s => {
      // The slot request schema may carry a single date; tolerate both shapes.
      const d = s.date || s.requested_date;
      if (d) push(d, { kind: "customer_off", label: `Customer off: ${s.customer_name || ""}`, color: "bg-purple-100 text-purple-900 border-purple-300" });
    });
    return map;
  }, [leaves, slotReqs]);

  return (
    <div className="space-y-6">
      {/* Manual chef leave entry */}
      <div className="card-default">
        <div className="flex items-center gap-2 mb-3">
          <Plus size={18} className="text-primary"/>
          <h3 className="heading text-lg font-semibold">Mark chef leave</h3>
        </div>
        <form onSubmit={submit} className="grid sm:grid-cols-4 gap-3" data-testid="add-leave-form">
          <div className="sm:col-span-1">
            <Label className="text-xs">Chef</Label>
            <Select value={chefId} onValueChange={setChefId}>
              <SelectTrigger data-testid="leave-chef"><SelectValue placeholder="Pick a chef"/></SelectTrigger>
              <SelectContent>
                {chefs.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={startDate} onChange={e=>{ setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }} data-testid="leave-from"/>
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" min={startDate} value={endDate} onChange={e=>setEndDate(e.target.value)} data-testid="leave-to"/>
          </div>
          <div className="sm:col-span-1 flex items-end">
            <Button type="submit" disabled={busy} className="rounded-full w-full bg-primary hover:bg-primary/90" data-testid="leave-submit">
              {busy ? "Saving…" : "Save leave"}
            </Button>
          </div>
          <div className="sm:col-span-4">
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea rows={2} value={reason} onChange={e=>setReason(e.target.value)} placeholder="Family emergency, vacation…" data-testid="leave-reason"/>
          </div>
        </form>
      </div>

      {/* Calendar view */}
      <div className="card-default">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <CalendarOff size={18} className="text-primary"/>
            <h3 className="heading text-lg font-semibold">{monthLabel(cursor)}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" onClick={()=>setCursor(c=>new Date(c.getFullYear(), c.getMonth()-1, 1))} data-testid="cal-prev"><ChevronLeft size={14}/></Button>
            <Button variant="outline" className="rounded-full" onClick={()=>setCursor(new Date())} data-testid="cal-today">Today</Button>
            <Button size="icon" variant="outline" onClick={()=>setCursor(c=>new Date(c.getFullYear(), c.getMonth()+1, 1))} data-testid="cal-next"><ChevronRight size={14}/></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {monthGrid.map((d, i) => {
            if (!d) return <div key={i} className="h-20 sm:h-24"/>;
            const date = ymd(d);
            const events = eventsByDate[date] || [];
            const today = date === todayYmd();
            return (
              <div key={i} className={`h-20 sm:h-24 p-1.5 rounded-lg border ${today ? "border-primary" : "border-border"} bg-card overflow-hidden`} data-testid={`cal-cell-${date}`}>
                <div className={`text-[11px] font-semibold ${today ? "text-primary" : "text-muted-foreground"}`}>{d.getDate()}</div>
                <div className="space-y-0.5 mt-0.5">
                  {events.slice(0,2).map((ev, j) => (
                    <div key={j} className={`text-[9px] px-1 py-0.5 rounded border truncate ${ev.color}`}>{ev.label}</div>
                  ))}
                  {events.length > 2 && <div className="text-[9px] text-muted-foreground">+{events.length-2} more</div>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-3 mt-4 text-xs text-muted-foreground flex-wrap">
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-200 border border-amber-300"/> Chef leave</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-purple-200 border border-purple-300"/> Customer unavailable</span>
        </div>
      </div>

      {/* Leave request list */}
      <div className="card-default space-y-2">
        <h3 className="heading text-lg font-semibold mb-2">All leaves</h3>
        {leaves.length === 0 && <p className="text-sm text-muted-foreground">No leaves on record.</p>}
        {leaves.map(l => (
          <div key={l.id} className="p-3 rounded-xl bg-muted/30 border border-border">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div>
                <div className="text-sm"><b>{l.chef_name}</b> · {l.start_date} → {l.end_date}</div>
                <div className="text-xs text-muted-foreground mt-1">{l.reason}</div>
                {l.source === "admin" && <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Entered by admin</div>}
              </div>
              <div className="flex items-center gap-2">
                <Badge className="rounded-full" variant="outline">{l.status}</Badge>
                {l.status === "pending" && onDecide && <>
                  <Button size="sm" variant="outline" onClick={()=>onDecide(l.id, "rejected")}>Reject</Button>
                  <Button size="sm" onClick={()=>onDecide(l.id, "approved")} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`approve-leave-${l.id}`}>Approve</Button>
                </>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
